import { CSSProperties, useEffect } from "react";

import { parseNumber } from "common/utils/numbers";
import { useMatch } from "common/router/hooks";
import Icon from "common/components/Icon";
import LinkButton from "common/components/LinkButton";
import { iconForCategorySlug } from "common/constants/categoryIcons";
import { useListSearchState } from "common/hooks";
import { Feature } from "common/types";

import Page from "client/common/components/layouts/Page";
import Container from "client/common/components/layouts/Container";
import Results from "client/common/components/results/Results";
import LoadingPage from "client/common/components/loading/LoadingPage";
import Error404Page from "client/common/components/errors/Error404Page";
import { useClinic, useFeatures } from "client/clinics/hooks";
import Body from "client/common/components/layouts/Body";
import Breadcrumbs from "client/common/components/content/Breadcrumbs";
import { Paths, urls } from "client/routes";
import { ProductListSource } from "client/analytics/types";

import CategoryContentLookup from "./CategoryContent";
import { useCategoryProductsQuery, useCategoryQuery } from "../hooks";
import { DEFAULT_PAGE_SIZE } from "../constants/filterDefinitions";
import { EMPTY_CATEGORY_PRODUCT_LINE_SELECTION, getProductFiltersFromSearch } from "../utils";
import BrowseSupport from "./BrowseSupport";
import PreFooter from "./PreFooter";
import ProductCardResults from "./ProductCardResults";
import CategorySubcategoryFilter from "./CategorySubcategoryFilter";
import ProductLineCarousel from "./ProductLineCarousel";

import styles from "../styles/CategoryPage.module.less";

const initialSearch = {
  ...EMPTY_CATEGORY_PRODUCT_LINE_SELECTION,
  category_ids: [],
  page: 1,
};

/** A category's page, listing one product card per product in the category. */
const CategoryPageV2 = () => {
  const match = useMatch<Paths["products"]["category"]>();
  const clinic = useClinic();
  const isCarouselV2 = useFeatures()[Feature.CarouselV2];
  const parsedId = parseNumber(match.params.id, 0);

  const [search, setSearch] = useListSearchState<Paths["products"]["category"]>(initialSearch);
  const filters = getProductFiltersFromSearch(search);

  const { data: category, isLoading: isCategoryLoading } = useCategoryQuery(parsedId);
  const {
    data: products,
    error: productsError,
    isLoading: areProductsLoading,
    isRefetching: areProductsRefetching,
    isPlaceholderData: areProductsStale,
  } = useCategoryProductsQuery(parsedId, {
    ...filters,
    page: parseNumber(search.page, 1),
    page_size: DEFAULT_PAGE_SIZE,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [search.page]);

  if (isCategoryLoading) {
    return <LoadingPage />;
  }

  if (!category) {
    return <Error404Page />;
  }

  const CategoryContent = CategoryContentLookup[category.slug] ?? CategoryContentLookup.default;
  const selectedSubcategories = category.children.filter((subcategory) =>
    filters.category_ids.includes(subcategory.id),
  );
  const productLinesScope =
    selectedSubcategories.length === 1 ? selectedSubcategories[0].name : category.extended_name;

  const renderResults = () => {
    if (areProductsLoading) {
      return <Results.Loading description="Loading products" />;
    }
    if (productsError || !products) {
      return <Results.FetchError error={productsError} entityName="product" />;
    }
    return (
      <ProductCardResults
        products={products}
        isRefetching={areProductsRefetching}
        isStale={areProductsStale}
        filters={filters}
        onFiltersChange={setSearch}
        onPageChange={(page) => setSearch({ page })}
        listSource={ProductListSource.CategoryPage}
        listContext={{ categoryId: parsedId }}
        entityName="product"
        keysHiddenFromPanel={["category"]}
      />
    );
  };

  return (
    <Page
      style={
        {
          "--gradient-start": CategoryContent.gradient.start,
          "--gradient-stop": CategoryContent.gradient.stop,
        } as CSSProperties
      }
      className={styles.page}
    >
      <div>
        <Container>
          {category.parent && (
            <Breadcrumbs
              className={styles.breadcrumbs}
              items={[
                <LinkButton
                  key="parent"
                  dest={urls.products.category({ params: { id: category.parent.id } })}
                >
                  {category.parent.name}
                </LinkButton>,
                category.extended_name,
              ]}
            />
          )}
          <div className={styles.top}>
            <div className={styles.heading}>
              <h1 className={styles.title}>{category.extended_name}</h1>
              <div className={styles.description}>{category.description}</div>
            </div>
            {isCarouselV2 ? (
              <Icon className={styles.icon} icon={iconForCategorySlug(category.slug)} />
            ) : (
              <CategoryContent.Icon className={styles.icon} />
            )}
          </div>
        </Container>
        <div className={styles.main}>
          <Body>
            {products && (
              <div className={styles.browse}>
                <CategorySubcategoryFilter
                  categoryId={parsedId}
                  subcategories={category.children}
                  categoryOptions={products.filters.categories}
                  selectedIds={filters.category_ids}
                  onChange={(category_ids) => setSearch({ category_ids, page: 1 })}
                />
                <ProductLineCarousel
                  heading={`Popular in ${productLinesScope}`}
                  categoryId={parsedId}
                  productLines={products.product_lines}
                />
              </div>
            )}
            {renderResults()}
          </Body>
        </div>
        <div className={styles.support}>
          <Container>
            <BrowseSupport />
          </Container>
        </div>
      </div>
      <PreFooter className={styles.preFooter} image={clinic.storefront_prefooter_image} />
    </Page>
  );
};

export default CategoryPageV2;
